/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { Typography } from '@one-thd/sui-atomic-components';
import PriceFormat from './PriceFormat';
import InstantRebates from './subcomponents/common/InstantRebates';
import helpers, { PRICE_TYPES } from '../../helpers/price-helper';
import { triggerNewRelicEvent } from '../../utils/utils';
import { SignInLink } from './subcomponents/signInLink/SignInLink';
import { CustomTooltip } from './subcomponents/common/CustomTooltip';

const {
  bool, number, oneOfType, shape, string,
} = proptypes;

export const PriceSimple = ({
  basic = false,
  channel,
  disableRangePricing = false,
  displayEachUom = false,
  displayDarkTheme = false,
  hideBadge = false,
  hideBulkPrice = false,
  hideLimitPerOrder = false,
  hidePromotions = false,
  hideRebates = false,
  hideSavingsText = false,
  omitDetails,
  skuModel,
  zipCode = null,
  useNewMapPolicy = false,
  isMapSpecialPriceViolation = false,
  stackWasPrice = false,
  isSignInToolTip = false,
  isPlpMessagingEnabled = false,
  priceAlignEnd = false,
  isDailyDealsEnabled = false,
  stackWasWithUnitPrice = false,
  priceBadge = null
}) => {
  const { pricing = {} } = skuModel || {};
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const stackedWasPrice = stackWasPrice || false;

  useEffect(() => {
    if (pricing?.itemId) {
      triggerNewRelicEvent({
        actionName: isCustomerIdentified ? 'PriceDisplayedAfterAuth' : 'MapPriceDisplayed',
        itemId: pricing?.itemId,
        layoutType: 'SimpleView',
        subExperience: 'PriceSimple',
        customerStatus: isCustomerIdentified ? 'authenticated' : 'unauthenticated',
        pricing,
        isCustomerIdentified // Pass the authentication status
      });
    }
  }, [isCustomerIdentified, pricing?.itemId, useNewMapPolicy]);

  const uomWrapper = classNames('sui-font-bold sui-text-xs sui-leading-none', {
    'sui-ml-2': !stackWasPrice,
    'sui-mt-1 sm:sui-mt-0': stackWasPrice,
    'sui-text-end': priceAlignEnd
  });
  const outerPriceWrapper = classNames('sui-flex sui-flex-col');
  const responsivePriceWrapper = classNames('sui-flex sui-leading-none', {
    'sui-flex-row sui-flex-wrap': stackWasPrice,
    'sui-flex-row sui-gap-x-2 sui-gap-y-1': !stackWasPrice,
    'sui-mt-2': !pricing?.quantityLimit && !pricing?.unitOfMeasureCoverage,
    'sui-items-end sui-text-right': priceAlignEnd,
  });
  const innerPriceWrapper = classNames('sui-flex sui-flex-row sui-leading-none', {
    'sui-justify-end': priceAlignEnd,
    'sui-mr-2': stackWasPrice,
    'sui-mr-0': !stackWasPrice
  });
  const strikethroughWrapper = classNames('sui-text-xs sui-leading-none sui-flex sui-flex-col', {});
  const greenTextWrapper = classNames({
    'sui-text-[#4CD693]': displayDarkTheme,
    'sui-text-success': !displayDarkTheme
  });
  const bulkWrapper = classNames({ 'sui-text-right': priceAlignEnd });
  const priceSimpleWrapper = classNames('sui-flex sui-flex-col ', { 'sui-items-end sui-text-right': priceAlignEnd });

  // BRIO Skus
  const handleBRIOSku = () => {
    const isCustomKitchenCabinetsSku = pricing?.category === 'Custom Kitchen Cabinets';
    const quoteMessage = isCustomKitchenCabinetsSku ? '' : 'Get a quote during a FREE design Consultation';
    const howToBuyDetails = `Home Depot Installation ${isCustomKitchenCabinetsSku ? 'Available' : 'Required'}`;
    return (
      <Typography component="div">
        <div className="sui-flex sui-flex-col">
          <div className="sui-text-lg sui-font-bold sui-break-words sui-mb-4">
            {quoteMessage}
          </div>
          <div className="sui-text-sm sui-break-words sui-mb-4">
            {howToBuyDetails}
          </div>
        </div>
      </Typography>
    );
  };

  // HIDE_PRICE/LIVE_GOODS
  const handleHidePrice = () => (
    <Typography variant="body-lg" weight="bold" component="div">
      <div className="sui-break-words">Contact your local store for price</div>
    </Typography>
  );

  // Unit price regular, Unit price with strike through
  const handleUnitPrice = ({
    alternatePrice, alternateUom, uom, price, priceBadge,
    save, pricingValues, unitPricingValues, bulkPrice, bulkPriceThresholdQty, hasEcoRebates
  }) => {
    const coveragePriceTemplate = helpers.handlePriceParsing(price, true);
    const { unitWasPrice, unitNowPrice } = pricing;
    const pricePerUnit = unitWasPrice || unitNowPrice;
    if (pricePerUnit) {
      const uomIsSqFt = uom === 'sq. ft.';
      const priceToDisplay = uomIsSqFt ? price : alternatePrice.toString();
      const wasPriceToDisplay = uomIsSqFt ? unitPricingValues : pricingValues;
      const showDollarOff = !uomIsSqFt;
      const unitPriceWrapper = classNames('sui-flex sui-flex-row sui-leading-none sui-text-xs', {
        'sui-ml-2': !stackWasPrice && !stackWasWithUnitPrice,
        'sm:sui-ml-2': stackWasPrice && !stackWasWithUnitPrice,
        'sui-mt-1 sm:sui-mt-0': !uomIsSqFt && stackWasPrice,
        'sui-ml-0 sm:sui-ml-0': stackWasWithUnitPrice,
      });

      return (
        <div id="unit-case-price" className={outerPriceWrapper}>
          {helpers.getLimitPerOrder(hideLimitPerOrder, pricing.quantityLimit, 'sui-text-xs sui-mb-2')}

          <div className={responsivePriceWrapper}>
            <div className={innerPriceWrapper}>
              <PriceFormat
                price={priceToDisplay}
                priceBadge={priceBadge}
                hideBadge={hideBadge}
                priceAlignEnd={priceAlignEnd}
              />
            </div>
            {uomIsSqFt && (
              <div className={outerPriceWrapper}>
                <span className={uomWrapper}>{uom && helpers.parseUom(uom, displayEachUom)}</span>
                <div className={unitPriceWrapper}>
                  <span>({helpers.handlePriceParsing(unitPricingValues ? alternatePrice : pricing.nowPrice, false)}</span>
                  <span>{`${pricing.uom && helpers.parseUom(pricing.uom, displayEachUom)})`}</span>
                </div>
              </div>
            )}
            {!uomIsSqFt && (
              <div className={outerPriceWrapper}>
                <span className={uomWrapper}>{alternateUom && helpers.parseUom(alternateUom, displayEachUom)}</span>
                {pricing.unitsPerCase > 1 && (
                  <span className={unitPriceWrapper}>
                    ({helpers.handlePriceParsing(price, false)} <span>{uom && helpers.parseUom(uom, displayEachUom)}</span>)
                  </span>
                )}
              </div>
            )}
          </div>
          {wasPriceToDisplay && (
            <div className="sui-text-xs sui-leading-none sui-flex sui-flex-col sui-mt-1">
              <span className="sui-text-subtle">{helpers.getWasPrice(wasPriceToDisplay)}</span>
              <span className={greenTextWrapper}>
                {helpers.getSavingsText(true, hideSavingsText, save, showDollarOff)}
              </span>
            </div>
          )}
          <div className={bulkWrapper}>
            {helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty)}
            {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
          </div>
        </div>
      );
    }

    return (
      <div id="unit-price">
        {pricing.unitOfMeasureCoverage && (
          <div className="sui-mb-1">
            {`${pricing.unitOfMeasureCoverage} (${coveragePriceTemplate} ${uom && helpers.parseUom(uom, displayEachUom)})`}
          </div>
        )}
        <div className={responsivePriceWrapper}>
          <PriceFormat
            price={alternatePrice.toString()}
            priceBadge={priceBadge}
            hideBadge={hideBadge}
            priceAlignEnd={priceAlignEnd}
          />
          {!pricingValues && (
            <span className={uomWrapper}>{alternateUom && helpers.parseUom(alternateUom, displayEachUom)}</span>
          )}
          {pricingValues
            && (
              <div className={strikethroughWrapper}>
                <span>{alternateUom && helpers.parseUom(alternateUom, displayEachUom)}</span>
                <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
              </div>
            )}
        </div>
        <div className={bulkWrapper}>
          {helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty)}
          {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
        </div>
      </div>
    );
  };

  // Was price
  const handleWasPrice = ({
    pricingValues, save, priceBadge, uom
  }) => {

    return (
      <div id="was-price">
        <div className={responsivePriceWrapper}>
          <div className={innerPriceWrapper}>
            <PriceFormat
              price={pricingValues.nowPrice}
              priceBadge={priceBadge}
              hideBadge={hideBadge}
              priceAlignEnd={priceAlignEnd}
            />
            {uom && !stackedWasPrice && (
              <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)} </span>
            )}
          </div>
          <div className={strikethroughWrapper}>
            {uom && stackedWasPrice && (
              <span className="sui-font-bold sui-text-xs">{helpers.parseUom(uom, displayEachUom)} </span>
            )}
            <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
            <span className={greenTextWrapper}>{helpers.getSavingsText(false, hideSavingsText, save)}</span>
          </div>

        </div>

      </div>
    );
  };

  // Starting At price
  const handleStartingAt = ({
    pricingValues, save, price, priceBadge, uom
  }) => {
    return (
      <div id="start-at">
        <div className="sui-font-regular sui-text-base sui-leading-none sui-mb-2">Starting at</div>
        <div className={responsivePriceWrapper}>
          <div className={innerPriceWrapper}>
            <PriceFormat
              price={price}
              priceBadge={priceBadge}
              hideBadge={hideBadge}
              priceAlignEnd={priceAlignEnd}
            />
            {uom && (
              <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)} </span>
            )}
          </div>
          <div className={strikethroughWrapper}>
            <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
            <span className={greenTextWrapper}>{helpers.getSavingsText(false, hideSavingsText, save)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Map price
  const handleMapPrice = ({
    mapPrice, pricingValues, uom, priceBadge, pricingMessage, hasEcoRebates, mapPolicy, save, hasStrikeThrough
  }) => {
    const tooltipContent = 'We\'ve set this item\'s price lower than the manufacturer will let us advertise, so you\'ll have to add it to your cart to see our price. If you decide you don\'t want to purchase the item, you can remove it from your cart.';

    hasStrikeThrough = useNewMapPolicy ? hasStrikeThrough : true;

    let priceToDisplay = '';
    const savingsExists = save?.dollarOff && save?.percentageOff;
    if (mapPrice) {
      priceToDisplay = mapPrice.wasPrice;
      hasStrikeThrough = mapPrice.hasStrikeThrough;
    } else if (pricingValues) {
      priceToDisplay = useNewMapPolicy && pricingValues.nowPrice ? pricingValues.nowPrice : pricingValues.wasPrice;
    }

    let mapPolicyMessage;
    if (useNewMapPolicy && mapPolicy === 'showSpecialPriceIfAuth' && !isCustomerIdentified) {
      mapPolicyMessage = (
        <SignInLink
          layoutType="simple"
          itemId={pricing?.itemId}
          displayDarkTheme
          isSignInToolTip={isSignInToolTip}
        />
      );
    } else if (mapPolicy !== 'showSpecialPriceIfAuth') {
      mapPolicyMessage = (
        <CustomTooltip pricingMessage={pricingMessage} channel={channel}>
          {tooltipContent}
        </CustomTooltip>
      );
    }
    return (
      <div className={outerPriceWrapper}>
        <div className="sui-inline-block">
          {(mapPrice || pricingValues) && (
            <div className={responsivePriceWrapper}>
              <div className={innerPriceWrapper}>
                <PriceFormat
                  price={priceToDisplay}
                  priceBadge={priceBadge}
                  hideBadge={hideBadge}
                  hasStrikeThrough={hasStrikeThrough}
                  priceAlignEnd={priceAlignEnd}
                />
                {(uom && priceToDisplay) && (
                  <span className="sui-ml-1">{helpers.parseUom(uom, displayEachUom)}</span>
                )}
              </div>
              {(useNewMapPolicy && savingsExists) && (
                <div className={strikethroughWrapper}>
                  <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                  <span className={greenTextWrapper}>{helpers.getSavingsText(false, hideSavingsText, save)}</span>
                </div>
              )}
            </div>
          )}
          {mapPolicyMessage}
        </div>
        {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
      </div>
    );
  };

  // Limit per order
  const handleLimitPerOrder = ({
    pricingValues, priceBadge, uom, save, price, hasEcoRebates,
    bulkPrice, bulkPriceThresholdQty,
    mapPrice, pricingMessage, mapPolicy, hasStrikeThrough
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    if (pricing.quantityLimit) {
      return (
        <div className={outerPriceWrapper}>
          {helpers.getLimitPerOrder(hideLimitPerOrder, pricing.quantityLimit, 'sui-text-xs sui-mb-2')}
          <div>
            {(mapPrice || pricingMessage) && handleMapPrice({
              mapPrice, pricingValues, uom, priceBadge, pricingMessage, mapPolicy, save, hasStrikeThrough
            })}
            {(!mapPrice && !pricingMessage) && (
              <div className={responsivePriceWrapper}>
                <div className={innerPriceWrapper}>
                  <PriceFormat
                    price={priceToDisplay}
                    priceBadge={priceBadge}
                    hideBadge={hideBadge}
                    priceAlignEnd={priceAlignEnd}
                  />
                  {uom && (
                    <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)}</span>
                  )}
                </div>
                <div className={strikethroughWrapper}>
                  <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                  <span className={greenTextWrapper}>
                    {helpers.getSavingsText(false, hideSavingsText, save)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className={bulkWrapper}>
            {helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty)}
            {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
          </div>
        </div>
      );
    }
    return '';
  };

  // Bulk price
  const handleBulkPrice = ({
    price, priceBadge, uom, pricingValues, save, bulkPrice, bulkPriceThresholdQty
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    return (
      <div id="bulk-price" className={outerPriceWrapper}>
        <div className={responsivePriceWrapper}>
          <div className={innerPriceWrapper}>
            <PriceFormat
              price={priceToDisplay}
              priceBadge={priceBadge}
              hideBadge={hideBadge}
              priceAlignEnd={priceAlignEnd}
            />
            {uom && (
              <span className={uomWrapper}>{uom && helpers.parseUom(uom, displayEachUom)}</span>
            )}
          </div>
          <div className={strikethroughWrapper}>
            <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
            <span className={greenTextWrapper}>{helpers.getSavingsText(false, hideSavingsText, save)}</span>
          </div>
        </div>
        <span className={bulkWrapper}>{helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty)}</span>
      </div>
    );
  };

  // Instant Rebate
  const handleInstantRebate = ({
    price, priceBadge, uom, pricingValues
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    return (
      <div id="instant-rebate" className={outerPriceWrapper}>
        <div className={responsivePriceWrapper}>
          <div className={innerPriceWrapper}>
            <PriceFormat
              price={priceToDisplay}
              priceBadge={priceBadge}
              hideBadge={hideBadge}
              priceAlignEnd={priceAlignEnd}
            />
            {uom && (
              <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)}</span>
            )}
          </div>
          <div className={strikethroughWrapper}>
            <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
          </div>
        </div>
        {!hideRebates && (
          <InstantRebates
            storePromotion={pricing?.storePromotion}
            price={pricing}
          />
        )}
      </div>
    );
  };

  // Range Price
  const handleRangePrice = ({
    minPrice, maxPrice, wasMinPrice, wasMaxPrice, save
  }) => {
    return (
      <div id="range-price">
        <div className="sui-flex sui-flex-row sui-leading-none sui-gap-1">
          <PriceFormat
            price={minPrice}
            hideBadge={hideBadge}
            priceAlignEnd={priceAlignEnd}
          />
          <span className="sui-font-bold sui-text-3xl"> - </span>
          <PriceFormat
            price={maxPrice}
            hideBadge={hideBadge}
            priceAlignEnd={priceAlignEnd}
          />
        </div>
        <div className={strikethroughWrapper}>
          <span className="sui-text-subtle">{helpers.getWasPriceRange(wasMinPrice, wasMaxPrice)}</span>
          <span className="sui-text-success">{helpers.getSavingsText(true, hideSavingsText, save, false)}</span>
        </div>
      </div>
    );
  };

  // Eco Rebate
  const handleEcoRebates = ({
    price, priceBadge, uom, pricingValues, save,
    mapPrice, pricingMessage, bulkPrice, bulkPriceThresholdQty, mapPolicy, hasStrikeThrough
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    return (
      <div id="eco-rebate-price">
        {(bulkPrice && !hideBulkPrice) && handleBulkPrice({
          price, priceBadge, uom, displayEachUom, pricingValues, save, bulkPrice, bulkPriceThresholdQty
        })}
        {(mapPrice || pricingMessage) && handleMapPrice({
          mapPrice, pricingValues, uom, priceBadge, pricingMessage, mapPolicy, save, hasStrikeThrough
        })}
        {(!mapPrice && !pricingMessage && !bulkPrice) && (
          <div className={responsivePriceWrapper}>
            <div className={innerPriceWrapper}>
              <PriceFormat
                price={priceToDisplay}
                priceBadge={priceBadge}
                hideBadge={hideBadge}
                priceAlignEnd={priceAlignEnd}
              />
              {uom && (
                <span className={uomWrapper}>{uom && helpers.parseUom(uom, displayEachUom)}</span>
              )}
            </div>
            <div className={strikethroughWrapper}>
              <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
              <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
            </div>
          </div>
        )}
        <span>{helpers.getRebates(hideRebates, true, pricing.itemId, zipCode, channel)}</span>
      </div>
    );
  };

  // No details
  const handleNoDetailsPrice = ({
    price, uom, pricingValues, save, priceBadge
  }) => {
    return (
      <div id="no-details-price">
        <div className={responsivePriceWrapper}>
          <div className={innerPriceWrapper}>
            <PriceFormat
              price={price}
              priceBadge={priceBadge}
              hideBadge={hideBadge}
              priceAlignEnd={priceAlignEnd}
            />
            {uom && (
              <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)}</span>
            )}
          </div>
          <div className={strikethroughWrapper}>
            <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
            {helpers.getSavingsText(false, hideSavingsText, save)}
          </div>
        </div>
      </div>
    );
  };

  // Standard price
  const handleStandardPrice = ({
    price, priceBadge, uom
  }) => {
    return (
      <div id="standard-price" className={innerPriceWrapper}>
        <PriceFormat
          price={price}
          priceBadge={priceBadge}
          hideBadge={hideBadge}
          priceAlignEnd={priceAlignEnd}
        />
        {uom && (
          <span className="sui-font-bold sui-text-xs sui-ml-2">{helpers.parseUom(uom, displayEachUom)}</span>
        )}
      </div>
    );
  };

  // Basic price
  const handleBasicPrice = ({ price }) => {
    return (
      <PriceFormat
        basic
        price={price}
        priceAlignEnd={priceAlignEnd}
      />
    );
  };

  const renderByType = ({ _price, type }) => {
    try {
      const { pricingValues, price, alternatePrice } = _price;
      if (!price && !pricingValues && !alternatePrice) {
        // new relic event for missing price, pricingValues, and alternatePrice
        triggerNewRelicEvent({
          actionName: 'PriceSimpleError',
          itemId: pricing?.itemId,
          layoutType: 'SimpleView',
          subExperience: 'PriceSimple',
          customerStatus: isCustomerIdentified ? 'authenticated' : 'unauthenticated',
          pricing,
          isCustomerIdentified, // Pass the authentication status
          error: 'No price to display.',
        });
      }
      switch (type) {
        case PRICE_TYPES.STANDARD:
          return handleStandardPrice(_price);
        case PRICE_TYPES.BASIC:
          return handleBasicPrice(_price);
        case PRICE_TYPES.STRIKE_THROUGH:
          return handleWasPrice(_price);
        case PRICE_TYPES.STARTING_AT:
          return handleStartingAt(_price);
        case PRICE_TYPES.MAP_PRICE:
          return handleMapPrice(_price);
        case PRICE_TYPES.ECO_REBATE:
          return handleEcoRebates(_price);
        case PRICE_TYPES.INSTANT_REBATE:
          return handleInstantRebate(_price);
        case PRICE_TYPES.BULK_PRICE:
          return handleBulkPrice(_price);
        case PRICE_TYPES.UNIT_AND_CASE:
          return handleUnitPrice(_price);
        case PRICE_TYPES.LIMIT_PER_ORDER:
          return handleLimitPerOrder(_price);
        case PRICE_TYPES.RANGE_PRICE:
          return handleRangePrice(_price);
        case PRICE_TYPES.BRIO:
          return handleBRIOSku();
        case PRICE_TYPES.LIVE_GOODS:
          return handleHidePrice();
        case PRICE_TYPES.NO_DETAILS:
          return handleNoDetailsPrice(_price);
        default: return null;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const savingCenterPromoMessage = helpers.getSavingCenterPromoMessage(
    pricing?.savingsCenterPromos,
    isPlpMessagingEnabled,
    pricing?.experienceTag
  );

  return (
    <div className={priceSimpleWrapper} data-testid="price-simple">
      {renderByType(helpers.getPricingObject({
        skuModel, displayEachUom, omitDetails, basic, disableRangePricing, useNewMapPolicy, isDailyDealsEnabled, isMapSpecialPriceViolation
      }))}
      {/* for savings center promos message */}
      {(!skuModel.preferredPriceFlag && !!savingCenterPromoMessage && !hidePromotions) && (
        <>
          <div className="sui-mb-2 sui-mt-2">
            <Typography variant="body-sm">{savingCenterPromoMessage}</Typography>
          </div>
          <CustomTooltip pricingMessage="See Details">
            Save when you buy multiple. See final price in cart.
          </CustomTooltip>
        </>
      )}
    </div>
  );
};

PriceSimple.propTypes = {
  basic: bool,
  channel: string.isRequired,
  disableRangePricing: bool,
  displayEachUom: bool,
  displayDarkTheme: bool,
  hideBadge: bool,
  hideBulkPrice: bool,
  hideLimitPerOrder: bool,
  hidePromotions: bool,
  hideSavingsText: bool,
  hideRebates: bool,
  omitDetails: bool,
  skuModel: shape({
    pricing: shape({
      itemId: oneOfType([number, string]),
      uom: string,
      unitsPerCase: number,
      wasPrice: oneOfType([number, string]),
      nowPrice: oneOfType([number, string]),
      specialBuyPrice: oneOfType([number, string]),
      dollarOff: oneOfType([number, string]),
      percentageOff: oneOfType([number, string]),
      specialBuySavings: oneOfType([number, string]),
      specialBuyDollarOff: oneOfType([number, string]),
      alternatePriceDisplay: bool,
      savingsCenterPromos: string,
      quantityLimit: oneOfType([number, string]),
      unitWasPrice: oneOfType([number, string]),
      storePromotion: shape(),
      unitOfMeasureCoverage: string
    }).isRequired
  }).isRequired,
  zipCode: string,
  priceBadge: string,
  /* Indicates whether to dynamically stack was price and savings price under main price */
  stackWasPrice: bool,
  useNewMapPolicy: bool,
  isMapSpecialPriceViolation: bool,
  isPlpMessagingEnabled: bool,
  isSignInToolTip: bool,
  isDailyDealsEnabled: bool,
  priceAlignEnd: bool,
  stackWasWithUnitPrice: bool
};
